/*
  Open Goaaal sections (og-*): behaviour for the native rebuild of the former
  GemPages homepage and soccer goals lander, plus promo pages. Vanilla JS, no
  dependencies. Add to cart is handled by the theme's own <product-form> element.
*/
(() => {
  const mobileQuery = window.matchMedia('(max-width: 767px)');

  /* Image banner: a different link on mobile, if one is set. */
  function initBannerLinks(root) {
    root.querySelectorAll('[data-og-href-mobile]').forEach((link) => {
      const desktopHref = link.getAttribute('href');
      const apply = () => {
        link.setAttribute('href', mobileQuery.matches ? link.dataset.ogHrefMobile : desktopHref);
      };
      apply();
      mobileQuery.addEventListener('change', apply);
    });
  }

  /* Muted background videos play only while on screen. */
  function initAutoplayVideos(root) {
    const videos = root.querySelectorAll('video[data-og-autoplay]');
    if (!videos.length) return;
    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => video.play().catch(() => {}));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (video.preload === 'none') video.preload = 'auto';
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.25 });
    videos.forEach((video) => observer.observe(video));
  }

  /* Click-to-load YouTube embed. */
  function initLiteYoutube(root) {
    root.querySelectorAll('[data-og-youtube]').forEach((box) => {
      const button = box.querySelector('.og-video__btn');
      if (!button) return;
      button.addEventListener('click', () => {
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${box.dataset.ogYoutube}?autoplay=1&mute=1&controls=1&playsinline=1&rel=0`;
        iframe.title = box.dataset.title || 'Video';
        iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        box.appendChild(iframe);
        button.remove();
      }, { once: true });
    });
  }

  /* Accordions that keep a single item open. */
  function initSingleAccordions(root) {
    root.querySelectorAll('[data-og-single]').forEach((group) => {
      group.querySelectorAll(':scope > details').forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          group.querySelectorAll(':scope > details[open]').forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* Scroll position at which a slide sits at the snap start of its track. */
  function slideTarget(track, slide) {
    const paddingLeft = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    const offset = slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    return Math.max(0, Math.min(offset - paddingLeft, track.scrollWidth - track.clientWidth));
  }

  /* Index of the slide nearest to the scroll position. */
  function nearestIndex(track, slides) {
    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slideTarget(track, slide) - track.scrollLeft);
      if (distance < bestDistance) { bestDistance = distance; best = index; }
    });
    return best;
  }

  function scrollToSlide(track, slide, smooth = true) {
    track.scrollTo({ left: slideTarget(track, slide), behavior: smooth ? 'smooth' : 'instant' });
  }

  /* Numbered dots for a scroll-snap row (set-up steps, reviews on mobile). */
  function initDots(root) {
    root.querySelectorAll('[data-og-dots-for]').forEach((dots) => {
      const track = root.querySelector(`#${dots.dataset.ogDotsFor}`);
      if (!track) return;
      const slides = Array.from(track.children);
      const buttons = Array.from(dots.querySelectorAll('button'));
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => scrollToSlide(track, slides[index]));
      });
      const update = () => {
        const active = nearestIndex(track, slides);
        buttons.forEach((button, index) => {
          button.classList.toggle('is-active', index === active);
          button.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      };
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      update();
    });
  }

  /* Previous / next buttons for a scroll-snap row (reviews on mobile). */
  function initArrows(root) {
    root.querySelectorAll('[data-og-arrows-for]').forEach((nav) => {
      const track = root.querySelector(`#${nav.dataset.ogArrowsFor}`);
      if (!track) return;
      const slides = Array.from(track.children);
      const go = (step) => {
        const count = slides.length;
        const index = (((nearestIndex(track, slides) + step) % count) + count) % count;
        scrollToSlide(track, slides[index]);
      };
      const prev = nav.querySelector('[data-og-prev]');
      const next = nav.querySelector('[data-og-next]');
      if (prev) prev.addEventListener('click', () => go(-1));
      if (next) next.addEventListener('click', () => go(1));
    });
  }

  /* Scroll progress bar under the reviews row. */
  function initProgress(root) {
    root.querySelectorAll('[data-og-progress-for]').forEach((progress) => {
      const track = root.querySelector(`#${progress.dataset.ogProgressFor}`);
      const bar = progress.querySelector('.og-progress__bar');
      if (!track || !bar) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const count = track.children.length || 1;
        bar.style.width = `${Math.max(1 / count, 0.1) * 100}%`;
        const travel = progress.clientWidth - bar.clientWidth;
        bar.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * travel : 0}px)`;
      };
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* Buy box: size cards select the variant, the gallery follows. */
  function initBuyBoxes(root) {
    root.querySelectorAll('[data-og-buy]').forEach((box) => {
      const dataEl = box.querySelector('[data-og-variants]');
      if (!dataEl) return;
      let variants = [];
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      const idInput = box.querySelector('input[name="id"]');
      const atc = box.querySelector('[name="add"]');
      const atcLabel = atc ? atc.querySelector('[data-og-atc-label]') : null;
      const cards = Array.from(box.querySelectorAll('[data-og-variant-id]'));
      const track = box.querySelector('.og-gallery__track');
      const slides = track ? Array.from(track.children) : [];
      const thumbs = Array.from(box.querySelectorAll('.og-gallery__thumb'));

      const showMedia = (mediaId, smooth) => {
        if (!track || !mediaId) return;
        const slide = slides.find((s) => s.dataset.mediaId === String(mediaId));
        if (slide) scrollToSlide(track, slide, smooth);
      };

      const select = (variantId, { smooth = true, focus = false } = {}) => {
        const variant = variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return;
        cards.forEach((card) => {
          const active = card.dataset.ogVariantId === String(variant.id);
          card.classList.toggle('is-active', active);
          card.setAttribute('aria-checked', active ? 'true' : 'false');
          card.tabIndex = active ? 0 : -1;
          if (active && focus) card.focus();
        });
        if (idInput) idInput.value = variant.id;
        if (atc) {
          atc.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
          if (atcLabel) atcLabel.textContent = variant.available ? atc.dataset.labelAdd : atc.dataset.labelSoldout;
        }
        if (variant.media_id) showMedia(variant.media_id, smooth);
        box.dispatchEvent(new CustomEvent('og:variant:change', { bubbles: true, detail: { variant } }));
      };

      cards.forEach((card, index) => {
        card.addEventListener('click', () => select(card.dataset.ogVariantId));
        card.addEventListener('keydown', (event) => {
          const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            select(card.dataset.ogVariantId);
          } else if (keys[event.key]) {
            event.preventDefault();
            const next = cards[(index + keys[event.key] + cards.length) % cards.length];
            select(next.dataset.ogVariantId, { focus: true });
          }
        });
      });

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => showMedia(thumb.dataset.mediaId, true));
      });
      if (track && thumbs.length) {
        const updateThumbs = () => {
          const active = slides[nearestIndex(track, slides)];
          thumbs.forEach((thumb) => thumb.classList.toggle('is-active', active && thumb.dataset.mediaId === active.dataset.mediaId));
        };
        track.addEventListener('scroll', () => window.requestAnimationFrame(updateThumbs), { passive: true });
        updateThumbs();
      }

      const initial = cards.find((card) => card.classList.contains('is-active')) || cards[0];
      if (initial) select(initial.dataset.ogVariantId, { smooth: false });
    });
  }

  /* Lander product cards: image slider with thumbnails and arrows, quantity stepper. */
  function initLanderProducts(root) {
    root.querySelectorAll('[data-og-lprod]').forEach((box) => {
      const track = box.querySelector('.og-lprod__main');
      const slides = track ? Array.from(track.children) : [];
      const thumbRow = box.querySelector('.og-lprod__thumbs');
      const thumbs = thumbRow ? Array.from(thumbRow.children) : [];

      if (track && slides.length) {
        const go = (index) => {
          const count = slides.length;
          scrollToSlide(track, slides[((index % count) + count) % count]);
        };
        thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => go(index)));
        const prev = box.querySelector('[data-og-prev]');
        const next = box.querySelector('[data-og-next]');
        if (prev) prev.addEventListener('click', () => go(nearestIndex(track, slides) - 1));
        if (next) next.addEventListener('click', () => go(nearestIndex(track, slides) + 1));

        const update = () => {
          const active = nearestIndex(track, slides);
          thumbs.forEach((thumb, index) => {
            thumb.classList.toggle('is-active', index === active);
            thumb.setAttribute('aria-current', index === active ? 'true' : 'false');
          });
          const thumb = thumbs[active];
          if (!thumb || !thumbRow) return;
          const t = thumb.getBoundingClientRect();
          const r = thumbRow.getBoundingClientRect();
          if (t.left < r.left) thumbRow.scrollBy({ left: t.left - r.left });
          else if (t.right > r.right) thumbRow.scrollBy({ left: t.right - r.right });
        };
        track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
        update();
      }

      const input = box.querySelector('.og-qty__input');
      box.querySelectorAll('[data-og-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!input) return;
          const value = (parseInt(input.value, 10) || 1) + Number(button.dataset.ogQtyStep);
          input.value = Math.max(1, value);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* Countdown timers (promo hero, sticky deal bar). */
  function initCountdowns(root) {
    root.querySelectorAll('[data-og-countdown]').forEach((box) => {
      if (box.dataset.ogCountdownReady) return;
      const end = Date.parse(box.dataset.ogCountdown);
      if (Number.isNaN(end)) return;
      box.dataset.ogCountdownReady = 'true';
      const nums = {};
      box.querySelectorAll('[data-og-unit]').forEach((el) => { nums[el.dataset.ogUnit] = el; });
      const pad = (n) => String(n).padStart(2, '0');
      let timer = null;
      const tick = () => {
        const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (left === 0) {
          if (timer) window.clearInterval(timer);
          const message = box.dataset.ogExpired;
          if (message) {
            box.innerHTML = '';
            const p = document.createElement('p');
            p.className = 'og-countdown__expired';
            p.textContent = message;
            box.appendChild(p);
          } else {
            box.hidden = true;
          }
          return false;
        }
        if (nums.d) nums.d.textContent = pad(Math.floor(left / 86400));
        if (nums.h) nums.h.textContent = pad(Math.floor((left % 86400) / 3600));
        if (nums.m) nums.m.textContent = pad(Math.floor((left % 3600) / 60));
        if (nums.s) nums.s.textContent = pad(left % 60);
        return true;
      };
      if (tick()) timer = window.setInterval(tick, 1000);
    });
  }

  /*
    Sticky deal bar: slides in once the promo hero has scrolled out of view. It sits just below
    the theme's sticky header, which would otherwise cover it.
  */
  function initStickyBars(root) {
    root.querySelectorAll('[data-og-sticky]').forEach((bar) => {
      const hero = document.querySelector('[data-og-phero]');
      const headers = Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
      const place = () => {
        let bottom = 0;
        headers.forEach((el) => {
          const pos = getComputedStyle(el).position;
          if (pos === 'sticky' || pos === 'fixed') bottom = Math.max(bottom, el.getBoundingClientRect().bottom);
        });
        bar.style.top = `${Math.max(0, Math.round(bottom))}px`;
      };
      window.addEventListener('scroll', () => {
        if (bar.classList.contains('is-visible')) window.requestAnimationFrame(place);
      }, { passive: true });
      window.addEventListener('resize', place);
      const show = (visible) => {
        if (visible) place();
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        bar.inert = !visible;
      };
      if (!hero || !('IntersectionObserver' in window)) { show(true); return; }
      new IntersectionObserver(([entry]) => {
        show(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(hero);
    });
  }

  function init(root = document) {
    initBannerLinks(root);
    initAutoplayVideos(root);
    initLiteYoutube(root);
    initSingleAccordions(root);
    initDots(root);
    initArrows(root);
    initProgress(root);
    initBuyBoxes(root);
    initLanderProducts(root);
    initCountdowns(root);
    initStickyBars(root);
  }

  /*
    Height of what stays fixed at the top of the screen once the page scrolls: the theme's sticky
    header, plus the mobile deal bar when the target is below the promo hero (the bar shows there).
  */
  function stickyOffset(target) {
    let offset = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => {
      const pos = getComputedStyle(el).position;
      if (pos === 'sticky' || pos === 'fixed') offset = Math.max(offset, el.offsetHeight);
    });
    const bar = document.querySelector('[data-og-sticky]');
    const hero = document.querySelector('[data-og-phero]');
    if (bar && hero && getComputedStyle(bar).display !== 'none' && target.getBoundingClientRect().top > hero.getBoundingClientRect().bottom) {
      offset += bar.offsetHeight;
    }
    return offset;
  }

  /*
    Links inside og sections to an anchor on the same page (yard buttons, "/#shop") scroll smoothly.
    Capture phase + stopPropagation: other scripts on the page also handle anchor clicks and would
    cut the scroll short. An anchor inside a section scrolls that section to just below the sticky header.
  */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.og-section a[href*="#"]');
    if (!link || link.origin !== window.location.origin || link.pathname !== window.location.pathname || link.hash.length < 2) return;
    const id = decodeURIComponent(link.hash.slice(1));
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    // "Choose this size" buttons (data-og-select="Large") pick that size in the buy box they link to.
    const pick = link.dataset.ogSelect;
    const box = pick ? target.closest('[data-og-buy]') : null;
    if (box) {
      let variants = [];
      try { variants = JSON.parse(box.querySelector('[data-og-variants]').textContent); } catch (e) { /* no variant data */ }
      const variant = variants.find((v) => v.title.toLowerCase() === pick.trim().toLowerCase());
      const card = variant ? box.querySelector(`[data-og-variant-id="${variant.id}"]`) : null;
      if (card) card.click();
    }
    const section = target.classList.contains('og-section') ? target : (target.closest('.og-section') || target);
    // The sticky header height decides where the section lands; scroll-margin-top (sized for the header,
    // for plain #hash page loads) only applies when nothing is sticky.
    const sticky = stickyOffset(section);
    const offset = sticky > 0 ? sticky : (section === target ? (parseFloat(getComputedStyle(target).scrollMarginTop) || 0) : 0);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: section.getBoundingClientRect().top + window.scrollY - offset, behavior: reduce ? 'auto' : 'smooth' });
    if (window.history && window.history.replaceState) window.history.replaceState(null, '', `#${id}`);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* Theme editor: re-initialise a section when it is re-rendered. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
